import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getLookups } from '../../redux/actions/lookups/lookups'
import { setSaveResultSuccess } from '../../redux/actions/saveData/saveData'
// import { INTERNAL_IN_PROGRESS } from '../../redux/actionTypes/actionsTypes'
import { RESET_LOOKUPS } from '../../redux/actionTypes/actionsTypes'
import { LookupsState } from '../../redux/reducers/getLookups/lookupsReducer'
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer'
import { SaveDataState } from '../../redux/reducers/getSaveData/saveDataReducer'
import IncomeAttributeLookupObject from '../../types/lookups/IncomeAttributeLookupObject'
import './Summary.css'
import IncomeExpenseSummary from './IncomeExpenseSummary'
import PropertySummary from './PropertySummary'
import UnitSummary from './UnitSummary'
import SuiteSummary from './SuiteSummary'
import BedCounts from '../PIE/BedCounts/BedCounts'
import OccupancyStatistics from '../PIE/OccupancyStatistics/OccupancyStatistics'
import NumberOfSites from '../PIE/NumberOfSites/NumberOfSites'
import RoundsRate from '../PIE/RoundsRate'
import { getSummaryData } from '../../redux/actions/summary/summary'
import { getSummaryDataState } from '../../redux/reducers/getSummaryData/summaryDataReducer'
import { ProfileAttributes } from '../../types/profile/ProfileAttributes'
import { commaSeparate, formatCurrency } from '../../utils/formatter'
import i18n from '../../i18n'
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../components/common/AccordionItem/AccordionItem'
import { selectLookups, selectProfileGetOverview, selectSaveData, selectSummaryData } from '../../redux/selectors/exportSelectorPaths'

export default function Summary(): JSX.Element {

    const dispatch = useDispatch()
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const incomeAttributeLookupType = profileOverview.profile.profile_type_code.toLowerCase()
    const lookupState: LookupsState = useSelector(selectLookups)
    const currencySymbol = "$";
    const saveData: SaveDataState = useSelector(selectSaveData)
    const summaryData: getSummaryDataState = useSelector(selectSummaryData);
    const { t } = useTranslation(['pie', 'room_statistics', 'navigation', 'number_of_sites', 'rounds_rate', 'occupancy_statistics', 'summary']);
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const hasLookupData = lookupState.success && !!lookupState.lookups
    const isPierProfileType = ["bip", "htl", "mtl", "ltc", "rho", "lle", "glf"].includes(incomeAttributeLookupType)
    const isRevenueTotalDetailProfileType = ["bip", "htl", "mtl", "ltc", "rho", "glf"].includes(incomeAttributeLookupType)
    const hasReserveSummarySection = ["htl", "mtl", "rho", "glf", "ltc"].includes(incomeAttributeLookupType)
    const hasCapitalSummarySection = ["bip", "htl", "mtl", "rho", "lle", "glf", "ltc"].includes(incomeAttributeLookupType)
    const hasSuiteSummarySection = ["bip", "htl", "mtl", "ltc", "lle", "glf"].includes(incomeAttributeLookupType)

    useEffect(() => {
        const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") ? (sessionStorage.getItem("currentSnapshotId") as string) : null;
        currentSnapshotId && dispatch(getSummaryData(profileOverview.profile.profile_type_code, currentSnapshotId));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const lookupType = incomeAttributeLookupType + "-income-attributes";
        if (!lookupState.loading && (lookupState.lookupType !== lookupType || !lookupState.success)
            && (["bip", "glf", "htl", "lle", "ltc", "mtl", "rho"].includes(incomeAttributeLookupType))
        ) {
            dispatch({ type: RESET_LOOKUPS });
            dispatch(getLookups(lookupType));
        }
    }, [dispatch, lookupState, incomeAttributeLookupType])

    useEffect(() => {
        if (saveData.saving) {
            dispatch(setSaveResultSuccess())
            dispatch({ type: RESET_LOOKUPS })
            dispatch(getLookups(incomeAttributeLookupType))
        }
    }, [dispatch, incomeAttributeLookupType, saveData.saving])

    const findAttributeData = (attributeCode: string) => {
        let incomeAttributesData: Record<string, unknown> | undefined = undefined;
        if (summaryData.data && (summaryData.data as Record<string, unknown>)["income_attributes"]) {
            incomeAttributesData = ((summaryData.data as Record<string, unknown>)["income_attributes"] as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
        }
        return incomeAttributesData;
    }

    const initTotalIncome = (showDetails = true, ignoreFields?: string[]) => {
        let total = 0;
        let incomeTotals: Record<string, number> = {};
        if (incomeAttributeLookupType === "ltc") {
            const incomeAttributesData = findAttributeData("28");
            const subsidyTotal = incomeAttributesData ? (incomeAttributesData.value ?? 0) as number : 0;
            incomeTotals["subsidy"] = subsidyTotal;
            total = total + subsidyTotal
        }
        const keys = Object.keys(lookupState.lookups[1]);
        keys.forEach((key) => {
            let currentTotal = 0;
            const isRentalLossKey = key === "rentalLoss";
            const lookupAttributes = lookupState.lookups[1][key] as IncomeAttributeLookupObject[];
            lookupAttributes.forEach((attribute) => {
                if (attribute.activeFlag === true) {
                    const code = attribute.incomeAttributeCode;
                    const incomeAttributesData = findAttributeData(code);
                    if (!(incomeAttributeLookupType === "ltc" && (code === "159" || code === "28"))) {
                        if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
                            if (!isRentalLossKey) {
                                total = total + (incomeAttributesData.value as number);
                            }
                            currentTotal = currentTotal + (incomeAttributesData.value as number);
                        }
                    }
                }
            });
            (!ignoreFields || (ignoreFields && !(ignoreFields.includes(key)))) && (incomeTotals[key] = currentTotal);
        });
        if (!showDetails) {
            incomeTotals = {};
        }
        (!ignoreFields || (ignoreFields && !(ignoreFields.includes("grandTotal")))) && (incomeTotals["grandTotal"] = total)
        return incomeTotals;
    }

    const initTotalExpenseOrCost = (type: 2 | 3 | 4, showDetails = true, ignoreFields?: string[]) => {
        let total = 0;
        let expenseTotals: Record<string, number> = {};
        const keys = Object.keys(lookupState.lookups[type]);
        keys.forEach((key) => {
            let currentTotal = 0;
            const lookupAttributes = lookupState.lookups[type][key] as IncomeAttributeLookupObject[];
            lookupAttributes.forEach((attribute) => {
                if (attribute.activeFlag === true) {
                    const code = attribute.incomeAttributeCode;
                    const incomeAttributesData = findAttributeData(code);
                    if (incomeAttributesData && incomeAttributesData.value && incomeAttributesData.value !== 0) {
                        if (!ignoreFields || (ignoreFields && !(ignoreFields.includes(key)))) {
                            total = total + (incomeAttributesData.value as number);
                        }
                        currentTotal = currentTotal + (incomeAttributesData.value as number);
                    }
                }
            });
            (!ignoreFields || (ignoreFields && !(ignoreFields.includes(key)))) && (expenseTotals[key] = currentTotal);
        });
        if (!showDetails) {
            expenseTotals = {};
        }
        (!ignoreFields || (ignoreFields && !(ignoreFields.includes("grandTotal")))) && (expenseTotals["grandTotal"] = total)
        return expenseTotals;
    }

    const getTotalOperatingExpenses = () => {
        const nonOperatingKeys = ["amortizationDepreciation", "nonOperatingIncomeExpenses", "GLF.nonOperatingIncomeExpenses"].filter((key) =>
            Object.keys(lookupState.lookups[2] || {}).includes(key)
        );

        return initTotalExpenseOrCost(
            2,
            true,
            ["replacementReserve", "reserve", ...nonOperatingKeys].filter((key) =>
                Object.keys(lookupState.lookups[2] || {}).includes(key)
            )
        );
    }

    const getTotalNonOperatingExpenses = () => {
        const nonOperatingKeys = ["amortizationDepreciation", "nonOperatingIncomeExpenses", "GLF.nonOperatingIncomeExpenses"].filter((key) =>
            Object.keys(lookupState.lookups[2] || {}).includes(key)
        );

        return initTotalExpenseOrCost(
            2,
            true,
            Object.keys(lookupState.lookups[2] || {}).filter((key) => !nonOperatingKeys.includes(key))
        );
    }

    // leaving incase for future use if we want to show total expenses again
    // const getTotalExpenses = () => {
    //     const operatingTotals = getTotalOperatingExpenses();
    //     const nonOperatingTotals = getTotalNonOperatingExpenses();
    //     const operatingTotal = operatingTotals["grandTotal"] || 0;
    //     const nonOperatingTotal = nonOperatingTotals["grandTotal"] || 0;
    //     return { grandTotal: operatingTotal + nonOperatingTotal };
    // }

    return (
        <div className='summary-container'>
            <p className='summary-heading'>{t("heading", { ns: 'summary' })}</p>
            {summaryData.success === true && 
            <div className='summary-page-container'>
                {["htl", "mtl"].includes(incomeAttributeLookupType) &&
                    <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("roomTableHeader", { ns: 'room_statistics' })}
                            key="roomTabHeader"
                            itemKey="roomTabHeader"
                            headingLevel={2}>
                            <div className='summary-grid'>
                                <div className='summary-section-container'>
                                    <table className='summary-table room-statistics-summary' role='presentation'>
                                        <thead className='invisible-header'><tr><td></td></tr></thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {t("roomNumOfRoomHeader", { ns: 'room_statistics' })}
                                                </td>
                                                <td className='summary-right-aligned-cell'>
                                                    {(summaryData.data as Record<string, unknown>)["profile_attributes"] && ("numberOfRoom" in ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes))
                                                        && ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["numberOfRoom"] ? commaSeparate(((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["numberOfRoom"]) : ""}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t("roomRoomsAvailableHeader", { ns: 'room_statistics' })}
                                                </td>
                                                <td className='summary-right-aligned-cell'>
                                                    {(summaryData.data as Record<string, unknown>)["profile_attributes"] && ("roomAvailable" in ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes))
                                                        && ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["roomAvailable"] ? commaSeparate(((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["roomAvailable"]) : ""}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t("roomRoomsSoldHeader", { ns: 'room_statistics' })}
                                                </td>
                                                <td className='summary-right-aligned-cell'>
                                                    {(summaryData.data as Record<string, unknown>)["profile_attributes"] && ("roomSold" in ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes))
                                                        && ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["roomSold"] ? commaSeparate(((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["roomSold"]) : ""}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t("roomOccupancyHeader", { ns: 'room_statistics' })}
                                                </td>
                                                <td className='summary-right-aligned-cell'>
                                                    {(summaryData.data as Record<string, unknown>)["profile_attributes"] && ("occupancyPercent" in ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes))
                                                        && ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["occupancyPercent"] ? ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["occupancyPercent"] + " %" : ""}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {t("roomAvgDailyHeader", { ns: 'room_statistics' })}
                                                </td>
                                                <td className='summary-right-aligned-cell'>
                                                    {(summaryData.data as Record<string, unknown>)["profile_attributes"] && ("averageDailyRate" in ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes))
                                                        && ((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["averageDailyRate"] ? currencySymbol + " " + formatCurrency(((summaryData.data as Record<string, unknown>)["profile_attributes"] as ProfileAttributes)["averageDailyRate"], i18n.language) : ""}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </AccordionItem>
                    </ControlledAccordion>
                }
                {["ltc"].includes(incomeAttributeLookupType) && <>
                    {((summaryData.data as Record<string, unknown>)["profile_attributes"]) && <BedCounts readonly={true} profileAttributes={(summaryData.data as Record<string, unknown>)["profile_attributes"] as Record<string, number | null>} />}
                </>}
                {["rho"].includes(incomeAttributeLookupType) && <>
                    {((summaryData.data as Record<string, unknown>)["profile_attributes"]) && <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("occupancyStats", { ns: 'navigation' })}
                            key="occupancyStatistics"
                            itemKey="occupancyStatistics"
                            headingLevel={2}>
                            <div className='summary-grid'><div className='summary-section-container'><OccupancyStatistics readonly={true} profileAttributes={(summaryData.data as Record<string, unknown>)["profile_attributes"] as Record<string, number | null>} /></div></div>
                        </AccordionItem>
                    </ControlledAccordion>}
                </>}
                {["lle"].includes(incomeAttributeLookupType) && <>
                    {((summaryData.data as Record<string, unknown>)["profile_attributes"]) && <ControlledAccordion providerValue={providerValue}>
                        <AccordionItem header={t("numberOfSitesTableHeader", { ns: 'number_of_sites' })}
                            key="numberOfSitesTable"
                            itemKey="numberOfSitesTable"
                            headingLevel={2}>
                            <div className='summary-grid'><div className='summary-section-container'><NumberOfSites readonly={true} profileAttributes={(summaryData.data as Record<string, unknown>)["profile_attributes"] as Record<string, number | null>} /></div></div>
                        </AccordionItem>
                    </ControlledAccordion>
                    }
                </>}
                {["glf"].includes(incomeAttributeLookupType) &&
                    <>{((summaryData.data as Record<string, unknown>)["profile_attributes"]) &&
                        <RoundsRate readonly={true} profileAttributes={(summaryData.data as Record<string, unknown>)["profile_attributes"] as Record<string, number | null>} />}</>}
                {isPierProfileType && <>{((summaryData.data as Record<string, unknown>)["income_attributes"]) && <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem header={t("incomeExpense", { ns: 'summary' })}
                        key="incomeExpense"
                        itemKey="incomeExpense"
                        headingLevel={2}>
                        <div className='summary-grid'>
                            {isPierProfileType && hasLookupData && (
                                <IncomeExpenseSummary
                                    currencySymbol={currencySymbol}
                                    totalHeader={t('revenues.totalRevenueIncome', { ns: 'pie' }) as string}
                                    tableData={initTotalIncome(isRevenueTotalDetailProfileType)}
                                />
                            )}

                            {isPierProfileType && hasLookupData && (
                                <div>
                                    <IncomeExpenseSummary
                                        currencySymbol={currencySymbol}
                                        totalHeader={t('analysis.totalOperatingExpenses', { ns: 'pie' }) as string}
                                        tableData={getTotalOperatingExpenses()}
                                    />
                                    <IncomeExpenseSummary
                                        currencySymbol={currencySymbol}
                                        totalHeader={t('analysis.totalNonOperatingExpenses', { ns: 'pie' }) as string}
                                        tableData={getTotalNonOperatingExpenses()}
                                    />
                                </div>
                            )}
                        </div>

                        <div className='summary-grid'>
                            {hasReserveSummarySection && hasLookupData && (
                                <IncomeExpenseSummary
                                    currencySymbol={currencySymbol}
                                    totalHeader={t('reserve.Total', { ns: 'pie' }) as string}
                                    tableData={initTotalExpenseOrCost(4, false)}
                                />
                            )}

                            {hasCapitalSummarySection && hasLookupData && (
                                <IncomeExpenseSummary
                                    currencySymbol={currencySymbol}
                                    totalHeader={t('costs.totalCapitalExp', { ns: 'pie' }) as string}
                                    tableData={initTotalExpenseOrCost(
                                        3,
                                        ["lle"].includes(incomeAttributeLookupType),
                                        (["lle"].includes(incomeAttributeLookupType) ? ["grandTotal"] : undefined)
                                    )}
                                    separateTables={["lle"].includes(incomeAttributeLookupType)}
                                />
                            )}
                        </div>
                    </AccordionItem>
                </ControlledAccordion>
                }</>}
                {isPierProfileType && <>{((summaryData.data as Record<string, unknown>)["profile_attributes"]) && <PropertySummary />}</>}
                {isPierProfileType && <>{((summaryData.data as Record<string, unknown>)["tenant_summary"]) && <UnitSummary />}</>}
                {hasSuiteSummarySection && <>{((summaryData.data as Record<string, unknown>)["apartment_summary"]) && <SuiteSummary />} </>}
                {["rho"].includes(incomeAttributeLookupType) && <>
                    {((summaryData.data as Record<string, unknown>)["4"]) && <SuiteSummary unitType='4' />}
                    {((summaryData.data as Record<string, unknown>)["5"]) && <SuiteSummary unitType='5' />}
                    {((summaryData.data as Record<string, unknown>)["6"]) && <SuiteSummary unitType='6' />}
                </>}
            </div>
            }
        </div>
    )
}
